import type { AnswerPayload, MonitorFlagKind } from '../../shared/protocol';
import type { ExamState } from '../../shared/exams';
import type { RecorderTransport } from './recorder';
import { ApiError, getToken } from './api';

/**
 * Cliente de los exámenes para quien los hace.
 *
 * No hace falta cuenta: al empezar, el servidor da un token del intento que se
 * guarda en este navegador. Con él se puede recargar la página o volver a
 * abrir el enlace y seguir donde se iba, con el mismo reloj.
 */

const KEY = (code: string) => `dahu_exam_${code.toUpperCase()}`;

export interface SavedAttempt {
  attemptId: string;
  token: string;
}

export function savedAttempt(code: string): SavedAttempt | null {
  try {
    const raw = localStorage.getItem(KEY(code));
    return raw ? (JSON.parse(raw) as SavedAttempt) : null;
  } catch {
    return null;
  }
}

export function saveAttempt(code: string, attempt: SavedAttempt): void {
  try {
    localStorage.setItem(KEY(code), JSON.stringify(attempt));
  } catch {
    /* sin almacenamiento: el intento dura lo que la pestaña */
  }
}

export function forgetAttempt(code: string): void {
  try {
    localStorage.removeItem(KEY(code));
  } catch {
    /* nada que borrar */
  }
}

/** Enlace directo a un examen: abre la pantalla de entrada con el código puesto. */
export function examUrl(code: string): string {
  return `${location.origin}/e/${encodeURIComponent(code)}`;
}

/** Código del examen si la página se abrió desde su enlace (`/e/<código>`). */
export function examCodeFromPath(): string | null {
  const match = /^\/e\/(\d{6})\/?$/.exec(location.pathname);
  return match ? match[1] : null;
}

async function call<T>(path: string, init: RequestInit & { examToken?: string } = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) headers.set('Content-Type', 'application/json');
  if (init.examToken) headers.set('X-Exam-Token', init.examToken);
  const session = getToken();
  if (session) headers.set('Authorization', `Bearer ${session}`);

  const res = await fetch(`/api${path}`, { ...init, headers, keepalive: init.keepalive });
  let data: unknown = null;
  try {
    data = await res.json();
  } catch {
    /* sin cuerpo */
  }
  if (!res.ok) {
    throw new ApiError((data as { error?: string } | null)?.error ?? `Error ${res.status}`, res.status);
  }
  return data as T;
}

export const examApi = {
  state: (a: SavedAttempt) =>
    call<{ state: ExamState }>(`/exam-attempts/${a.attemptId}`, { examToken: a.token }).then((r) => r.state),

  start: (body: { code: string; firstName: string; lastName: string; consent: boolean }) =>
    call<{ token: string; state: ExamState }>('/exam-access/start', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  answer: (a: SavedAttempt, questionId: string, payload: AnswerPayload | null) =>
    call<{ ok: true }>(`/exam-attempts/${a.attemptId}/answer`, {
      method: 'PUT',
      examToken: a.token,
      body: JSON.stringify({ questionId, payload }),
    }),

  submit: (a: SavedAttempt) =>
    call<{ state: ExamState }>(`/exam-attempts/${a.attemptId}/submit`, {
      method: 'POST',
      examToken: a.token,
    }).then((r) => r.state),
};

/** Transporte del grabador para un intento: los eventos van por HTTP. */
export function examTransport(a: SavedAttempt): RecorderTransport {
  const post = (path: string, body: unknown) => {
    void call(`/exam-attempts/${a.attemptId}/${path}`, {
      method: 'POST',
      examToken: a.token,
      body: JSON.stringify(body),
      // Que el último lote llegue aunque se cierre la pestaña.
      keepalive: JSON.stringify(body).length < 60_000,
    }).catch(() => {
      /* un lote perdido no detiene el examen */
    });
  };
  return {
    online: () => navigator.onLine,
    sendEvents: (events) => post('rec', { events }),
    sendFlag: (kind: MonitorFlagKind) => post('flag', { kind }),
  };
}
